import { borderRadius, EmphasisText, zIndexes, styled } from "@iventis/styles";
import { Theme, css } from "@emotion/react";
import { ExtractRefFromComponent, OptionalExceptFor } from "@iventis/types/useful.types";
import { Status } from "@iventis/domain-model/model/status";
import React, { useState, useEffect, PropsWithChildren, forwardRef, useImperativeHandle, useMemo, ComponentPropsWithoutRef } from "react";
import { VirtuosoGrid } from "react-virtuoso";

import { Asset } from "@iventis/domain-model/model/asset";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFunctionality } from "@iventis/utilities";
import { LoadingComponent } from "./loading";
import { SelectableImageComponent } from "./selectable-image";

// Redecalare forwardRef
declare module "react" {
    function forwardRef<T, P = {}>(render: (props: P, ref: React.Ref<T>) => React.ReactElement | null): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

// eslint-disable-next-line no-underscore-dangle
function _AssetBrowserComponent<TAsset extends OptionalExceptFor<Asset, "id" | "name">>(
    {
        assets,
        imageUrlGetter,
        fetchNextPage,
        isFetchingNextPage,
        cardWidth,
        cardHeight,
        className,
        initialSelection,
        cardMinWidth,
        multiSelect = true,
        translate = (text) => text,
        onSelectionChange,
        onEditClicked,
    }: PropsWithChildren<{
        assets: TAsset[];
        imageUrlGetter: (asset: TAsset) => string;
        fetchNextPage: () => void;
        isFetchingNextPage: boolean;
        cardWidth: string;
        cardHeight: string;
        cardMinWidth?: string;
        className?: string;
        initialSelection?: string;
        multiSelect?: boolean;
        translate?: (text: string) => string;
        onSelectionChange?: (selection: string[]) => void;
        onEditClicked?: (id: string) => void;
    }>,
    ref: React.ForwardedRef<{ getSelection: () => string[] }>
) {
    const [selection, setSelection] = useState<string[]>(initialSelection !== undefined ? [initialSelection] : []);
    const select = (id: string) => setSelection((state) => (multiSelect ? [...state, id] : [id]));
    const deselect = (id: string) => setSelection((state) => state.filter((x) => x !== id));

    const functionality = useFunctionality();

    const activeAssets = useMemo(() => assets.filter((asset) => asset.status === Status.Active), [assets]);

    // Sync selection
    useEffect(() => onSelectionChange?.(selection), [selection]);

    useImperativeHandle(ref, () => ({
        getSelection() {
            return selection;
        },
    }));

    const GridList = useMemo(
        () =>
            React.forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>((props, listRef) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <StyledGridList ref={listRef} {...props} cardWidth={cardWidth} cardMinWidth={cardMinWidth} />
            )),
        [cardWidth, cardMinWidth]
    );

    if (activeAssets.length === 0) {
        return (
            <StyledContainer className={className} cardWidth={cardWidth} cardMinWidth={cardMinWidth}>
                <EmphasisText size="small" className="no-results">
                    {translate("No results")}
                </EmphasisText>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer className={className} cardWidth={cardWidth} cardMinWidth={cardMinWidth}>
            <VirtuosoGrid
                style={{ height: "100%", width: "100%" }}
                totalCount={activeAssets.length}
                components={{ List: GridList }}
                endReached={() => fetchNextPage()}
                overscan={200}
                itemContent={(index) => {
                    const asset = activeAssets[index];
                    return (
                        <div className="image-container">
                            <SelectableImageComponent
                                id={asset.id}
                                name={asset.name}
                                selected={selection.includes(asset.id)}
                                select={select}
                                deselect={deselect}
                                image={imageUrlGetter(asset)}
                                width={cardWidth}
                                height={cardHeight}
                                onEditClicked={onEditClicked}
                            >
                                <div className="icon-container">
                                    {(asset.metaData?.sdf || asset.metaData?.customColour) && <FontAwesomeIcon icon={["fas", "palette"]} className="metadata-icon" />}
                                    {asset.metaData?.customImage && functionality?.imagesOnModels && <FontAwesomeIcon icon={["fas", "image"]} className="metadata-icon" />}
                                </div>
                            </SelectableImageComponent>
                        </div>
                    );
                }}
            />
            {isFetchingNextPage && <LoadingComponent />}
        </StyledContainer>
    );
}

const StyledGridList = styled.div<{ cardWidth: string; cardMinWidth?: string }>`
    display: grid;
    ${(props) =>
        props.cardWidth === "100%"
            ? css`
                  grid-template-columns: repeat(auto-fill, minmax(${props.cardMinWidth ?? props.cardWidth}, 1fr));
              `
            : css`
                  grid-template-columns: repeat(auto-fill, ${props.cardMinWidth ?? props.cardWidth});
              `}
    grid-auto-rows: min-content;
    gap: 10px;
    padding: 10px;
`;

const StyledContainer = styled.div<{ cardWidth: string; cardMinWidth?: string }>`
    height: 100%;
    min-height: 0;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-radius: ${borderRadius.standard};
    box-sizing: border-box;
    overflow: hidden;
    position: relative;

    .no-results {
        position: absolute;
        margin: 10px;
    }

    .image-container {
        height: 100%;
        width: 100%;
        position: relative;
    }

    .icon-container {
        position: absolute;
        bottom: 2px;
        right: 2px;
        display: flex;
        gap: 5px;
    }

    .metadata-icon {
        z-index: ${zIndexes.editLayer.sdfIcon};
        color: ${({ theme }: { theme: Theme }) => theme.primaryColors.subdued50};
    }
`;

export type AssetBrowserRef = ExtractRefFromComponent<typeof AssetBrowserComponent>;

export const AssetBrowserComponent = forwardRef(_AssetBrowserComponent);
